import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
    ArrowLeft,
    Activity,
    Plus,
    Trash2,
    HeartPulse,
    LineChart as LineChartIcon,
} from "lucide-react";
import { api } from "@/lib/api";
import { TID } from "@/constants/testIds";

const METRIC_OPTIONS = [
    { value: "bp", label: "Blood pressure", unit: "mmHg", hasSecondary: true, placeholder: "Systolic" },
    { value: "weight", label: "Weight", unit: "kg" },
    { value: "sugar_fasting", label: "Sugar (fasting)", unit: "mg/dL" },
    { value: "sugar_pp", label: "Sugar (post-meal)", unit: "mg/dL" },
    { value: "pulse", label: "Pulse", unit: "bpm" },
    { value: "temperature", label: "Temperature", unit: "°F" },
    { value: "spo2", label: "SpO₂", unit: "%" },
    { value: "waist", label: "Waist", unit: "cm" },
    { value: "custom", label: "Custom", unit: "" },
];

function metricLabel(entry) {
    if (entry.metric === "custom") return entry.custom_name || "Custom";
    const m = METRIC_OPTIONS.find((o) => o.value === entry.metric);
    return m?.label || entry.metric;
}

function displayValue(e) {
    if (e.metric === "bp") return `${Math.round(e.value)}/${Math.round(e.secondary)} ${e.unit || ""}`;
    return `${e.value}${e.unit ? " " + e.unit : ""}`;
}

/**
 * LineChart — SVG, no deps. Plots one metric's series.
 * For BP, plots systolic AND diastolic on the same chart.
 */
export function VitalsChart({ metric, entries, unit, custom_name, testId }) {
    const series = useMemo(() => {
        const sorted = [...entries].sort((a, b) => (a.date > b.date ? 1 : -1));
        return sorted;
    }, [entries]);

    if (series.length === 0) {
        return (
            <div className="grid h-40 place-items-center border border-dashed border-veda-border bg-veda-surface2/40 text-xs text-veda-muted">
                No entries yet.
            </div>
        );
    }

    const W = 560;
    const H = 180;
    const PAD_L = 40;
    const PAD_R = 12;
    const PAD_T = 14;
    const PAD_B = 26;
    const chartW = W - PAD_L - PAD_R;
    const chartH = H - PAD_T - PAD_B;

    const isBP = metric === "bp";
    const allYs = isBP
        ? series.flatMap((e) => [e.value, e.secondary].filter((v) => v != null))
        : series.map((e) => e.value);
    let yMin = Math.min(...allYs);
    let yMax = Math.max(...allYs);
    if (yMin === yMax) {
        yMin -= 1;
        yMax += 1;
    }
    const yRange = yMax - yMin;
    yMin = Math.max(0, yMin - yRange * 0.15);
    yMax = yMax + yRange * 0.15;
    const range = yMax - yMin || 1;

    const xAt = (i) => PAD_L + (series.length === 1 ? chartW / 2 : (chartW * i) / (series.length - 1));
    const yAt = (v) => PAD_T + chartH - ((v - yMin) / range) * chartH;

    function pathFor(vals) {
        return vals
            .map((v, i) => `${i === 0 ? "M" : "L"} ${xAt(i).toFixed(1)} ${yAt(v).toFixed(1)}`)
            .join(" ");
    }

    const primaryPath = pathFor(series.map((e) => e.value));
    const secondaryPath = isBP ? pathFor(series.map((e) => e.secondary || 0)) : null;

    const yTicks = 4;
    const ticks = Array.from({ length: yTicks + 1 }, (_, i) => yMin + (range * i) / yTicks);

    return (
        <div className="overflow-x-auto" data-testid={testId}>
            <svg viewBox={`0 0 ${W} ${H}`} className="w-full max-w-full">
                {/* y-axis grid */}
                {ticks.map((t, i) => (
                    <g key={i}>
                        <line
                            x1={PAD_L}
                            x2={W - PAD_R}
                            y1={yAt(t)}
                            y2={yAt(t)}
                            className="stroke-veda-divider"
                            strokeWidth="1"
                        />
                        <text
                            x={PAD_L - 6}
                            y={yAt(t) + 3}
                            textAnchor="end"
                            className="fill-veda-muted text-[9px]"
                            style={{ fontFamily: "system-ui" }}
                        >
                            {Math.round(t * 10) / 10}
                        </text>
                    </g>
                ))}
                {/* x-axis labels: first, mid, last */}
                {[0, Math.floor(series.length / 2), series.length - 1]
                    .filter((v, i, arr) => arr.indexOf(v) === i)
                    .map((i) => (
                        <text
                            key={i}
                            x={xAt(i)}
                            y={H - 8}
                            textAnchor="middle"
                            className="fill-veda-muted text-[9px]"
                            style={{ fontFamily: "system-ui" }}
                        >
                            {series[i].date}
                        </text>
                    ))}
                {/* series */}
                {secondaryPath && (
                    <path
                        d={secondaryPath}
                        fill="none"
                        className="stroke-veda-brand/40"
                        strokeWidth="1.6"
                    />
                )}
                <path
                    d={primaryPath}
                    fill="none"
                    className="stroke-veda-brand"
                    strokeWidth="2"
                />
                {series.map((e, i) => (
                    <g key={e.entry_id}>
                        <circle cx={xAt(i)} cy={yAt(e.value)} r="3" className="fill-veda-brand" />
                        {isBP && (
                            <circle
                                cx={xAt(i)}
                                cy={yAt(e.secondary)}
                                r="2.5"
                                className="fill-veda-brand/50"
                            />
                        )}
                    </g>
                ))}
            </svg>
            <p className="mt-1 text-[10px] text-veda-muted">
                {custom_name || metricLabel({ metric })}
                {unit ? ` · ${unit}` : ""}
                {isBP && " · solid=systolic, faded=diastolic"}
            </p>
        </div>
    );
}

function LogVitalForm({ onLogged }) {
    const [metric, setMetric] = useState("bp");
    const [value, setValue] = useState("");
    const [secondary, setSecondary] = useState("");
    const [customName, setCustomName] = useState("");
    const [customUnit, setCustomUnit] = useState("");
    const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
    const [note, setNote] = useState("");
    const [saving, setSaving] = useState(false);

    const opt = METRIC_OPTIONS.find((o) => o.value === metric);

    async function submit(e) {
        e.preventDefault();
        if (!value) {
            toast.error("Enter a value.");
            return;
        }
        setSaving(true);
        try {
            const payload = {
                metric,
                value: parseFloat(value),
                date,
                note: note.trim() || null,
            };
            if (opt.hasSecondary) {
                if (!secondary) {
                    toast.error("Enter diastolic value.");
                    setSaving(false);
                    return;
                }
                payload.secondary = parseFloat(secondary);
            }
            if (metric === "custom") {
                if (!customName.trim()) {
                    toast.error("Custom name is required.");
                    setSaving(false);
                    return;
                }
                payload.custom_name = customName.trim();
                payload.unit = customUnit.trim() || null;
            }
            await api.post("/patient/vitals", payload);
            setValue("");
            setSecondary("");
            setNote("");
            toast.success("Vital logged.");
            onLogged?.();
        } catch (err) {
            const d = err?.response?.data?.detail;
            toast.error(typeof d === "string" ? d : "Could not log vital.");
        } finally {
            setSaving(false);
        }
    }

    return (
        <form
            onSubmit={submit}
            data-testid={TID.vitalsAddForm}
            className="grid gap-3 border border-veda-border bg-veda-surface p-4 md:grid-cols-6"
        >
            <label className="md:col-span-2">
                <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-veda-muted">
                    Metric
                </span>
                <select
                    value={metric}
                    onChange={(e) => setMetric(e.target.value)}
                    data-testid={TID.vitalsMetric}
                    className="mt-1.5 w-full border border-veda-border bg-veda-surface px-3 py-2 text-sm focus:border-veda-brand focus:outline-none"
                >
                    {METRIC_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                            {o.label}
                            {o.unit ? ` (${o.unit})` : ""}
                        </option>
                    ))}
                </select>
            </label>

            {metric === "custom" ? (
                <>
                    <label>
                        <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-veda-muted">
                            Name
                        </span>
                        <input
                            type="text"
                            value={customName}
                            onChange={(e) => setCustomName(e.target.value)}
                            placeholder="e.g. Ferritin"
                            data-testid={TID.vitalsCustomName}
                            className="mt-1.5 w-full border border-veda-border bg-veda-surface px-3 py-2 text-sm focus:border-veda-brand focus:outline-none"
                        />
                    </label>
                    <label>
                        <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-veda-muted">
                            Unit
                        </span>
                        <input
                            type="text"
                            value={customUnit}
                            onChange={(e) => setCustomUnit(e.target.value)}
                            placeholder="ng/mL"
                            data-testid={TID.vitalsUnit}
                            className="mt-1.5 w-full border border-veda-border bg-veda-surface px-3 py-2 text-sm focus:border-veda-brand focus:outline-none"
                        />
                    </label>
                </>
            ) : null}

            <label className={metric === "custom" ? "md:col-span-1" : "md:col-span-1"}>
                <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-veda-muted">
                    {opt.hasSecondary ? "Systolic" : "Value"}
                </span>
                <input
                    type="number"
                    step="0.1"
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    placeholder={opt.placeholder || opt.unit}
                    data-testid={TID.vitalsValue}
                    className="mt-1.5 w-full border border-veda-border bg-veda-surface px-3 py-2 text-sm focus:border-veda-brand focus:outline-none"
                />
            </label>

            {opt.hasSecondary && (
                <label>
                    <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-veda-muted">
                        Diastolic
                    </span>
                    <input
                        type="number"
                        step="0.1"
                        value={secondary}
                        onChange={(e) => setSecondary(e.target.value)}
                        placeholder="e.g. 80"
                        data-testid={TID.vitalsSecondary}
                        className="mt-1.5 w-full border border-veda-border bg-veda-surface px-3 py-2 text-sm focus:border-veda-brand focus:outline-none"
                    />
                </label>
            )}

            <label className="md:col-span-1">
                <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-veda-muted">
                    Date
                </span>
                <input
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    data-testid={TID.vitalsDate}
                    max={new Date().toISOString().slice(0, 10)}
                    className="mt-1.5 w-full border border-veda-border bg-veda-surface px-3 py-2 text-sm focus:border-veda-brand focus:outline-none"
                />
            </label>

            <input
                type="text"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Note (optional)"
                maxLength={200}
                data-testid={TID.vitalsNote}
                className="border border-veda-border bg-veda-surface px-3 py-2 text-sm focus:border-veda-brand focus:outline-none md:col-span-3"
            />
            <button
                type="submit"
                disabled={saving}
                data-testid={TID.vitalsSubmit}
                className="inline-flex items-center justify-center gap-2 bg-veda-inverse px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-veda-onInverse disabled:opacity-60 md:col-span-1"
            >
                <Plus size={12} /> {saving ? "Saving…" : "Log"}
            </button>
        </form>
    );
}

export default function Vitals() {
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/patient/vitals", { params: { days: 365 } });
            setEntries(data?.entries || []);
        } catch {
            toast.error("Could not load vitals.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    async function del(id) {
        if (!window.confirm("Delete this entry?")) return;
        try {
            await api.delete(`/patient/vitals/${id}`);
            toast.success("Entry deleted.");
            load();
        } catch {
            toast.error("Could not delete.");
        }
    }

    // Group by metric (+ custom_name to keep custom series separate)
    const grouped = useMemo(() => {
        const g = new Map();
        for (const e of entries) {
            const key = e.metric === "custom" ? `custom::${e.custom_name || "Custom"}` : e.metric;
            if (!g.has(key)) g.set(key, []);
            g.get(key).push(e);
        }
        return g;
    }, [entries]);

    return (
        <div data-testid={TID.vitalsPage} className="pb-16">
            <div className="border-b border-veda-border">
                <div className="veda-container py-6 md:py-10">
                    <Link
                        to="/dashboard"
                        className="inline-flex items-center gap-1 text-xs font-medium text-veda-muted hover:text-veda-onSurface"
                    >
                        <ArrowLeft size={13} /> Dashboard
                    </Link>
                    <div className="mt-4">
                        <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-veda-brand">
                            Vitals timeline
                        </p>
                        <h1 className="font-display mt-1 text-3xl text-veda-onSurface md:text-5xl">
                            Track BP, weight, sugar, and more.
                        </h1>
                        <p className="mt-2 max-w-xl text-sm text-veda-muted">
                            Log a reading whenever you check. Your Vaidya sees
                            the same trends during your consult.
                        </p>
                    </div>
                </div>
            </div>

            <div className="veda-container py-8 md:py-12 space-y-8">
                <section className="space-y-3">
                    <div className="flex items-center gap-2">
                        <span className="grid h-8 w-8 place-items-center bg-veda-brand/10 text-veda-brand">
                            <Activity size={14} />
                        </span>
                        <div>
                            <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-veda-muted">
                                Log a reading
                            </p>
                            <p className="font-display text-lg text-veda-onSurface">
                                One tap, one metric.
                            </p>
                        </div>
                    </div>
                    <LogVitalForm onLogged={load} />
                </section>

                <section className="space-y-4">
                    <div className="flex items-center gap-2">
                        <span className="grid h-8 w-8 place-items-center bg-veda-brand/10 text-veda-brand">
                            <LineChartIcon size={14} />
                        </span>
                        <div>
                            <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-veda-muted">
                                Trends
                            </p>
                            <p className="font-display text-lg text-veda-onSurface">
                                {loading
                                    ? "Loading…"
                                    : entries.length === 0
                                    ? "No entries yet"
                                    : `${entries.length} readings across ${grouped.size} metric(s)`}
                            </p>
                        </div>
                    </div>
                    {grouped.size === 0 && !loading && (
                        <div className="border border-dashed border-veda-border bg-veda-surface2/40 p-8 text-center text-sm text-veda-muted">
                            Log your first reading above — a chart will appear as
                            soon as you have any entries.
                        </div>
                    )}
                    <div className="grid gap-6 lg:grid-cols-2">
                        {[...grouped.entries()].map(([key, items]) => {
                            const first = items[0];
                            const label =
                                first.metric === "custom"
                                    ? first.custom_name || "Custom"
                                    : metricLabel(first);
                            return (
                                <div
                                    key={key}
                                    className="border border-veda-border bg-veda-surface p-4"
                                >
                                    <div className="flex items-baseline justify-between">
                                        <p className="font-display text-base text-veda-onSurface">
                                            {label}
                                        </p>
                                        <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-veda-muted">
                                            {items.length} reading{items.length === 1 ? "" : "s"}
                                        </p>
                                    </div>
                                    <div className="mt-2">
                                        <VitalsChart
                                            metric={first.metric}
                                            unit={first.unit}
                                            custom_name={first.custom_name}
                                            entries={items}
                                            testId={TID.vitalsChart(
                                                first.metric === "custom"
                                                    ? `custom-${(first.custom_name || "x").toLowerCase().replace(/[^a-z0-9]+/g, "-")}`
                                                    : first.metric,
                                            )}
                                        />
                                    </div>
                                    <ul className="mt-3 divide-y divide-veda-divider text-xs">
                                        {items
                                            .slice()
                                            .reverse()
                                            .slice(0, 6)
                                            .map((e) => (
                                                <li
                                                    key={e.entry_id}
                                                    data-testid={TID.vitalsRow(e.entry_id)}
                                                    className="flex items-center justify-between py-2"
                                                >
                                                    <span className="text-veda-onSurface">
                                                        <span className="text-veda-muted tabular-nums">
                                                            {e.date}
                                                        </span>
                                                        {" · "}
                                                        <span className="font-semibold tabular-nums">
                                                            {displayValue(e)}
                                                        </span>
                                                        {e.note && (
                                                            <span className="ml-1 italic text-veda-muted">
                                                                — {e.note}
                                                            </span>
                                                        )}
                                                    </span>
                                                    <button
                                                        type="button"
                                                        onClick={() => del(e.entry_id)}
                                                        data-testid={TID.vitalsDelete(e.entry_id)}
                                                        className="text-veda-muted hover:text-veda-error"
                                                        aria-label="Delete entry"
                                                    >
                                                        <Trash2 size={12} />
                                                    </button>
                                                </li>
                                            ))}
                                    </ul>
                                </div>
                            );
                        })}
                    </div>
                </section>

                <p className="pt-4 text-[11px] text-veda-muted">
                    <HeartPulse size={11} className="mr-1 inline" />
                    Your vitals stay private to you and any Vaidya you're
                    consulting with.
                </p>
            </div>
        </div>
    );
}
